import { useCallback, useEffect, useState } from "react";

import { ConsentType } from "@/api/generated/models";
import { useUpdateConsents } from "@/api/mutations/consent";
import { useUpdateNotificationPreferences } from "@/api/mutations/userProfile";
import { useGetUserProfile } from "@/api/queries/userProfile";
import {
  H3,
  Header,
  Link,
  Paragraph,
  Section,
  Separator,
  ToggleSwitch,
  useMedia,
} from "@/components";
import { shortBrandName } from "@/config/brand";
import { SMS_CONSENT_PRIVACY_DISCLOSURE } from "@/constants";
import { useAnalytics } from "@/hooks/analytics";
import RevokeSmsConsentDialog from "@/settings/RevokeSmsConsentDialog";
import ScheduledNotifications from "@/settings/ScheduledNotifications";

export default function NotificationsSettingsScreen() {
  const { lg } = useMedia();
  const { data: userProfile } = useGetUserProfile();
  const { mutate: updateNotificationPreferences, isPending: isUpdating } =
    useUpdateNotificationPreferences();
  const { mutate: updateConsents, isPending: isUpdatingConsent } =
    useUpdateConsents();
  const { track } = useAnalytics();

  const [clinicEnabled, setClinicEnabled] = useState<boolean>(true);
  const [noriEnabled, setNoriEnabled] = useState<boolean>(true);
  const [smsConsentEnabled, setSmsConsentEnabled] = useState<boolean>(false);

  useEffect(() => {
    if (userProfile?.notificationPreferences) {
      setClinicEnabled(userProfile.notificationPreferences.clinic);
      setNoriEnabled(userProfile.notificationPreferences.nori);
    }
  }, [userProfile?.notificationPreferences]);

  useEffect(() => {
    if (userProfile?.consents) {
      setSmsConsentEnabled(
        Boolean(userProfile.consents[ConsentType.sms_consent_to_treat]),
      );
    }
  }, [userProfile?.consents]);

  const handleClinicToggle = useCallback(
    (enabled: boolean) => {
      const previousValue = clinicEnabled;
      setClinicEnabled(enabled);
      track("superclinical-__notification-preference-changed", {
        event_context: {
          notification_type: "clinic",
          enabled,
        },
      });
      updateNotificationPreferences(
        { data: { clinic: enabled } },
        {
          onError: () => {
            setClinicEnabled(previousValue);
          },
        },
      );
    },
    [clinicEnabled, updateNotificationPreferences, track],
  );

  const handleNoriToggle = useCallback(
    (enabled: boolean) => {
      const previousValue = noriEnabled;
      setNoriEnabled(enabled);
      track("superclinical-__notification-preference-changed", {
        event_context: {
          notification_type: "nori",
          enabled,
        },
      });
      updateNotificationPreferences(
        { data: { nori: enabled } },
        {
          onError: () => {
            setNoriEnabled(previousValue);
          },
        },
      );
    },
    [noriEnabled, updateNotificationPreferences, track],
  );

  const [showRevokeSmsDialog, setShowRevokeSmsDialog] = useState(false);

  const updateSmsConsent = useCallback(
    (enabled: boolean) => {
      const previousValue = smsConsentEnabled;
      setSmsConsentEnabled(enabled);
      updateConsents(
        {
          data: {
            consents: {
              [ConsentType.sms_consent_to_treat]: enabled,
            },
            source: "settings-toggle",
          },
        },
        {
          onSuccess: () => {
            setShowRevokeSmsDialog(false);
          },
          onError: () => {
            setSmsConsentEnabled(previousValue);
          },
        },
      );
    },
    [smsConsentEnabled, updateConsents],
  );

  const handleSmsConsentToggle = useCallback(
    (enabled: boolean) => {
      if (!enabled) {
        setShowRevokeSmsDialog(true);
        return;
      }
      updateSmsConsent(true);
    },
    [updateSmsConsent],
  );

  return (
    <Section margin="$screenPadding" flex={1} $lg={{ flex: 1, gap: "$200" }}>
      {lg && (
        <Header gap="$250" marginBottom="$50">
          <H3>Notifications</H3>
          <Separator borderColor="$fillNeutral" />
        </Header>
      )}
      <ToggleSwitch
        label="Consent to treat over SMS"
        description={`Allows you to receive full messages and reply directly via text (SMS). ${SMS_CONSENT_PRIVACY_DISCLOSURE}`}
        checked={smsConsentEnabled}
        onCheckedChange={handleSmsConsentToggle}
        disabled={isUpdatingConsent}
      />
      <Separator borderColor="$fillNeutral" />
      <ToggleSwitch
        label="Clinic Notifications"
        description="Includes messages from your provider, updates on lab orders, medication prescriptions, etc."
        checked={clinicEnabled}
        onCheckedChange={handleClinicToggle}
        disabled={isUpdating}
      />
      <Separator borderColor="$fillNeutral" />
      <ToggleSwitch
        label={`${shortBrandName} Notifications`}
        description="Includes system messages, post-visit check-ins, notifications you schedule, etc."
        checked={noriEnabled}
        onCheckedChange={handleNoriToggle}
        disabled={isUpdating}
      />
      <ScheduledNotifications />
      <Separator borderColor="$fillNeutral" />
      <Paragraph size="$xs" lineHeight="$sm" color="$textSecondary">
        If you are turning notifications back on, or are not receiving text
        messages, text 'START' to{" "}
        <Link
          href="sms:+16506435335"
          size="$xs"
          dataTrackingId="sms-notifications-number"
          bold
        >
          650-643-5335
        </Link>
        . Message and data rates may apply.
      </Paragraph>
      <RevokeSmsConsentDialog
        open={showRevokeSmsDialog}
        onOpenChange={setShowRevokeSmsDialog}
        onConfirmRevoke={() => updateSmsConsent(false)}
        isLoading={isUpdatingConsent}
      />
    </Section>
  );
}
